import { useForm } from 'react-hook-form';
import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import StarBorderIcon from '../../../assets/star-border-icon';
import StarIcon from '../../../assets/star-icon';
import InfoIcon from '../../../assets/info-icon';
import TextInput from '../../../components/text-input';
import { isValidPhoneNumber } from 'react-phone-number-input';
import TextArea from '../../../components/text-area';
import CommentIcon from '../../../assets/comment-icon';
import ArrowBackIcon from '../../../assets/arrow-back-icon';
import { RouteOriginToDestination } from './origin-destination-list-item';
import useMutate from '../../../hook/use-mutation';
import { API } from '../../../helpers/constants';
import SummationForm from '../../../components/summation-form';
import { getRandomNumber } from '../../../helpers/number';

const ReviewForm = ({ onSentReview, route, transportId }) => {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });
  const [securityValue1] = useState(getRandomNumber());
  const [securityValue2] = useState(getRandomNumber());
  const [rating, setRating] = useState(-1);
  const [errorRating, setErrorRating] = useState(false);
  const { mutate, isLoading } = useMutate({
    api: API.submitRate,
    options: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ['rateReview'] });
        onSentReview();
      },
      onError: () => {
        alert('Ulasan anda gagal dikirim, silahkan coba beberapa saat lagi');
      }
    }
  });

  const onClickRatingHandler = (index) => {
    setRating(index);
    if (errorRating) setErrorRating(false);
  };

  const StarRating = Array(5)
    .fill(<StarBorderIcon size={38} />)
    .map((_, index) => {
      if (index < rating + 1)
        return (
          <span className="cursor-pointer" onClick={() => onClickRatingHandler(index)}>
            <StarIcon size={38} />
          </span>
        );
      return (
        <span className="cursor-pointer" onClick={() => onClickRatingHandler(index)}>
          <StarBorderIcon size={38} />
        </span>
      );
    });

  const onSubmit = (e) => {
    e.preventDefault();

    const isEmptyRating = rating < 0;
    if (isEmptyRating) {
      setErrorRating(true);
    }

    handleSubmit((values) => {
      if (!isEmptyRating) {
        mutate({
          name: values?.name,
          ratting: rating + 1,
          review: values?.['review-message'],
          trayek: route,
          phone: values?.['phone-number'],
          vehicle: values?.['car-number'],
          angkutan_id: transportId
        });
      }
    })();
  };

  return (
    <form onSubmit={onSubmit}>
      <div className="flex gap-2 items-center text-left text-[#616161] text-base mb-2">
        <InfoIcon /> Harap memenuhi ketentuan berlaku
      </div>
      <div className="flex gap-2 justify-center mb-2">{StarRating.map((Item) => Item)}</div>
      {errorRating && <p className="text-red-500 text-left mb-1">Bintang tidak boleh kosong</p>}
      <div className="mb-2">
        <TextInput
          label="Nama"
          placeholder="Masukan nama anda"
          register={register('name', { required: 'Nama tidak boleh kosong' })}
          error={errors.name}
        />
      </div>
      <div className="mb-2">
        <TextInput
          label="Nomor HP"
          placeholder="Masukan Nomor HP anda"
          register={register('phone-number', {
            required: 'Nomor HP tidak boleh kosong',
            validate: {
              isValidPhoneNumber: (value) => {
                return isValidPhoneNumber(value, 'ID') || 'Nomor HP tidak valid';
              }
            }
          })}
          error={errors['phone-number']}
        />
      </div>
      <div className="mb-2">
        <TextInput
          label="Nomor Kendaraan"
          placeholder="Masukan Nomor Kendaraan yang digunakan"
          register={register('car-number', { required: 'Nomor Kendaraan tidak boleh kosong' })}
          error={errors['car-number']}
        />
      </div>
      <div className="mb-2">
        <TextArea
          label="Komentar"
          rows={2}
          placeholder="Masukan ulasan tentang bus pada rute ini"
          register={register('review-message', { required: true })}
          error={errors['review-message']}
        />
      </div>
      <div className="mb-2">
        <SummationForm
          value1={securityValue1}
          value2={securityValue2}
          register={register('answer', {
            required: 'Jawaban tidak boleh kosong',
            validate: (value) => {
              return Number(value) === securityValue1 + securityValue2 || 'Jawaban salah';
            }
          })}
          error={errors.answer}
        />
      </div>
      <button
        disabled={isLoading}
        type="submit"
        className="w-full text-body2 text-primary-500 font-medium flex justify-center items-center gap-2 bg-[#F2FAF7] border border-[#ACDEC7] rounded-lg px-3 py-2"
      >
        <CommentIcon size={24} />
        {isLoading ? 'Sedang diproses...' : 'Buat Ulasan'}
      </button>
    </form>
  );
};

const CreateReviewForm = ({
  onBack,
  busType,
  busClass,
  companyName,
  onSentReview,
  origin,
  destination,
  transportId
}) => {
  return (
    <div className="space-y-4">
      <div className="flex justify-between">
        <span className="flex gap-4 items-center cursor-pointer" onClick={onBack}>
          <ArrowBackIcon />
          <div className="flex gap-2">
            <span className="text-body1 font-bold md:text-2xl leading-[150%] md:leading-[100%]">
              Buat Ulasan
            </span>
          </div>
        </span>
        <div className="flex flex-col items-end">
          <div className="hidden md:flex justify-center items-center gap-1">
            <span className="flex text-left text-sm text-white px-2 py-0.5 bg-primary-500 rounded-full">
              {busType}
            </span>
            <span className="flex text-left text-sm text-white px-2 py-0.5 bg-gray-500 rounded-full">
              {busClass}
            </span>
          </div>
          <span className="text-body1 text-black md:text-2xl font-bold md:text-[#616161]">
            {companyName}
          </span>
        </div>
      </div>
      <RouteOriginToDestination origin={origin} destination={destination} />
      <hr className="my-2" />
      <ReviewForm
        onSentReview={onSentReview}
        route={`${origin?.name} - ${destination?.name}`}
        transportId={transportId}
      />
    </div>
  );
};

export default CreateReviewForm;
